import * as React from 'react'

import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ProjectResourceFailure } from '~/client/src/shared/resources/domain/projectResourceType'
import type { ResourceResult } from '~/client/src/shared/resources/domain/resourceResult'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import type ProjectResourcesStore from '~/client/src/shared/stores/domain/ProjectResources.store'

type RequireResourceSuccess<TDomainFailure> = <T>(
  result: ResourceResult<T, ProjectResourceFailure<TDomainFailure>>,
) => T

export interface ProjectResourceStoreView<TResource, TDomainFailure> {
  readonly records: IResourceRecord[]
  readonly recordsWithDeletedItems: IResourceRecord[]
  readonly loading: boolean
  readonly error: string | null
  readonly isReady: boolean
  readonly schema: ResourceSchema
  readonly store: ProjectResourcesStore<TResource, TDomainFailure>
  readonly save: (
    recordId: string | null,
    formData: Readonly<Record<string, unknown>>,
  ) => Promise<string | null>
  readonly bulkEdit: (input: {
    readonly resourceIds: readonly string[]
    readonly changes: Readonly<Record<string, unknown>>
  }) => Promise<void>
  readonly deleteMany: (ids: readonly string[]) => Promise<void>
}

const EMPTY_RECORDS: IResourceRecord[] = []
const EMPTY_TEMPLATES: ResourceSchema['templates'] = []
const EMPTY_PROPERTIES: ResourceSchema['properties'] = []

export function useProjectResourceStore<TResource, TDomainFailure>(
  projectId: string | undefined,
  store: ProjectResourcesStore<TResource, TDomainFailure>,
  requireSuccess: RequireResourceSuccess<TDomainFailure>,
): ProjectResourceStoreView<TResource, TDomainFailure> {
  const view = projectId
    ? store.viewFor(projectId)
    : {
        records: EMPTY_RECORDS,
        recordsWithDeletedItems: EMPTY_RECORDS,
        loading: false,
        error: null,
        isReady: false,
        schema: {
          templates: EMPTY_TEMPLATES,
          properties: EMPTY_PROPERTIES,
          allProperties: EMPTY_PROPERTIES,
          loading: false,
          error: null,
          isReady: false,
          propertiesLoading: false,
          propertiesError: null,
          propertiesReady: false,
        },
      }

  const requireProjectId = React.useCallback((): string => {
    if (!projectId) throw new Error('Missing projectId')
    return projectId
  }, [projectId])

  const execute = React.useCallback(
    async <T>(
      operation: (
        projectId: string,
      ) => Promise<ResourceResult<T, ProjectResourceFailure<TDomainFailure>>>,
    ): Promise<T> => requireSuccess(await operation(requireProjectId())),
    [requireProjectId, requireSuccess],
  )

  const schema = React.useMemo<ResourceSchema>(
    () => ({
      ...view.schema,
      refresh: () => execute(id => store.refreshSchema(id)),
      createForm: input => execute(id => store.createForm(id, input)),
      updateForm: (id, input) =>
        execute(projectId => store.updateForm(projectId, id, input)),
      deleteForms: ids =>
        execute(projectId => store.deleteForms(projectId, ids)),
      createProperty: input =>
        execute(projectId => store.createProperty(projectId, input)),
      updateProperty: (id, input) =>
        execute(projectId => store.updateProperty(projectId, id, input)),
      archiveProperty: id =>
        execute(projectId => store.archiveProperty(projectId, id)),
      unarchiveProperty: id =>
        execute(projectId => store.unarchiveProperty(projectId, id)),
      deleteProperty: id =>
        execute(projectId => store.deleteProperty(projectId, id)),
    }),
    [
      execute,
      store,
      view.schema.allProperties,
      view.schema.error,
      view.schema.isReady,
      view.schema.loading,
      view.schema.properties,
      view.schema.propertiesLoading,
      view.schema.propertiesError,
      view.schema.propertiesReady,
      view.schema.templates,
    ],
  )

  const save = React.useCallback(
    (recordId: string | null, formData: Readonly<Record<string, unknown>>) =>
      execute(projectId => store.save(projectId, { recordId, formData })),
    [execute, store],
  )
  const bulkEdit = React.useCallback(
    (input: {
      readonly resourceIds: readonly string[]
      readonly changes: Readonly<Record<string, unknown>>
    }) => execute(projectId => store.bulkEdit(projectId, input)),
    [execute, store],
  )
  const deleteMany = React.useCallback(
    (ids: readonly string[]) => execute(() => store.deleteMany(ids)),
    [execute, store],
  )

  return { ...view, schema, store, save, bulkEdit, deleteMany }
}
